import React, { useEffect, useRef, useState } from 'react'
import { Alert, Button, ColorPicker, Collapse, Flex, Select, Segmented, Slider, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { Configuration, defaultConfiguration, FormTheme, FormPalette, PreviewAppearance, presets } from '../public-form'
import { hasLowFormContrast } from '../theme'

function ColorControl({ label, value, onChange }: { label: string; value: string; onChange: (value: string) => void }) {
    const [draft, setDraft] = useState(value)
    const timer = useRef<ReturnType<typeof setTimeout>>(undefined)
    const callback = useRef(onChange)
    callback.current = onChange
    useEffect(() => {
        clearTimeout(timer.current)
        setDraft(value)
    }, [value])
    useEffect(() => () => clearTimeout(timer.current), [])
    return (
        <Flex
            vertical
            align="start"
            gap={4}
        >
            <Typography.Text>{label}</Typography.Text>
            <ColorPicker
                value={draft}
                showText
                disabledAlpha
                onChange={(color) => {
                    const hex = color.toHexString()
                    setDraft(hex)
                    clearTimeout(timer.current)
                    timer.current = setTimeout(() => callback.current(hex), 180)
                }}
                onChangeComplete={(color) => {
                    clearTimeout(timer.current)
                    callback.current(color.toHexString())
                }}
            >
                <Button aria-label={label}>
                    <span
                        className="inline-block size-4 rounded border"
                        style={{ backgroundColor: draft }}
                    />
                    {draft.toUpperCase()}
                </Button>
            </ColorPicker>
        </Flex>
    )
}
type BrandingStyleControlsProps = {
    value: Configuration
    set: (patch: Partial<Configuration>) => void
    previewAppearance: PreviewAppearance
    setPreviewAppearance: (appearance: PreviewAppearance) => void
}
export function BrandingStyleControls({
    value,
    set,
    previewAppearance,
    setPreviewAppearance,
}: BrandingStyleControlsProps) {
    const { t } = useTranslation()
    const activeAppearance = value.appearance === 'system' ? previewAppearance : value.appearance
    const visibleTheme = activeAppearance === 'dark' ? { ...value.theme, ...value.darkTheme } : value.theme
    const setTheme = (key: keyof FormTheme, next: string | number) => {
        if (activeAppearance === 'dark' && key.startsWith('color')) {
            set({ darkTheme: { ...value.darkTheme, [key as keyof FormPalette]: next } })
        } else set({ theme: { ...value.theme, [key]: next } })
    }
    const lowContrast = hasLowFormContrast(visibleTheme, activeAppearance)
    const selectedPreset = Object.keys(presets).find(
        (name) =>
            Object.entries(presets[name].theme).every(
                ([key, token]) => value.theme[key as keyof typeof value.theme] === token
            ) &&
            Object.entries(presets[name].darkTheme).every(
                ([key, token]) => value.darkTheme[key as keyof FormPalette] === token
            )
    )
    return (
        <Flex
            vertical
            gap="middle"
            className="public-form-branding-controls"
        >
            <label>
                <Typography.Text>{t('Theme')}</Typography.Text>
                <Select
                    className="mt-1 w-full"
                    aria-label={t('Theme')}
                    value={selectedPreset ?? 'Custom'}
                    options={[
                        ...Object.keys(presets).map((name) => ({ value: name, label: t(name) })),
                        ...(!selectedPreset ? [{ value: 'Custom', label: t('Custom'), disabled: true }] : []),
                    ]}
                    onChange={(name) => set(presets[name])}
                />
            </label>
            <label>
                <Typography.Text>{t('Published appearance')}</Typography.Text>
                <Select
                    className="mt-1 w-full"
                    aria-label={t('Published appearance')}
                    value={value.appearance}
                    onChange={(appearance) => set({ appearance })}
                    options={[
                        { label: t('System preference'), value: 'system' },
                        { label: t('Always light'), value: 'light' },
                        { label: t('Always dark'), value: 'dark' },
                    ]}
                />
            </label>
            {value.appearance === 'system' && (
                <>
                    <Typography.Text>{t('Edit and preview variant')}</Typography.Text>
                    <Segmented
                        block
                        value={previewAppearance}
                        options={[
                            { label: t('Light'), value: 'light' },
                            { label: t('Dark'), value: 'dark' },
                        ]}
                        onChange={setPreviewAppearance}
                    />
                    <Typography.Text type="secondary">
                        {t('Colors are saved separately for each variant. Font and sizing apply to both.')}
                    </Typography.Text>
                </>
            )}
            <ColorControl
                label={t('Brand color')}
                value={visibleTheme.colorPrimary}
                onChange={(v) => setTheme('colorPrimary', v)}
            />
            <Collapse
                defaultActiveKey={[]}
                items={[
                    {
                        key: 'appearance',
                        label: t('Advanced appearance'),
                        children: (
                            <Flex
                                vertical
                                gap="middle"
                            >
                                {(
                                    [
                                        ['Page background', 'colorBgLayout'],
                                        ['Form background', 'colorBgContainer'],
                                        ['Text color', 'colorText'],
                                    ] as const
                                ).map(([label, key]) => (
                                    <ColorControl
                                        key={key}
                                        label={t(label)}
                                        value={visibleTheme[key]}
                                        onChange={(v) => setTheme(key, v)}
                                    />
                                ))}
                                <label className="block">
                                    <Typography.Text>{t('Font')}</Typography.Text>
                                    <Select
                                        className="mt-1 w-full"
                                        aria-label={t('Font')}
                                        value={visibleTheme.fontFamily}
                                        onChange={(font) => setTheme('fontFamily', font)}
                                        options={[
                                            'Poppins, sans-serif',
                                            'Arial, sans-serif',
                                            'Georgia, serif',
                                            'system-ui, sans-serif',
                                            'monospace',
                                        ].map((font) => ({ value: font, label: font.split(',')[0] }))}
                                    />
                                </label>
                                <label>
                                    {t('Corner radius')} ({t('{{size}} px', { size: value.theme.borderRadius })})
                                    <Slider
                                        min={0}
                                        max={24}
                                        value={visibleTheme.borderRadius}
                                        onChange={(v) => setTheme('borderRadius', v)}
                                    />
                                </label>
                                <label>
                                    {t('Text size')} ({t('{{size}} px', { size: value.theme.fontSize })})
                                    <Slider
                                        min={16}
                                        max={20}
                                        value={visibleTheme.fontSize}
                                        onChange={(v) => setTheme('fontSize', v)}
                                    />
                                </label>
                                <label>
                                    {t('Input height')} ({t('{{size}} px', { size: value.theme.controlHeight })})
                                    <Slider
                                        min={44}
                                        max={56}
                                        value={visibleTheme.controlHeight}
                                        onChange={(v) => setTheme('controlHeight', v)}
                                    />
                                </label>
                                <Button
                                    onClick={() =>
                                        set({
                                            theme: defaultConfiguration().theme,
                                            darkTheme: defaultConfiguration().darkTheme,
                                        })
                                    }
                                >
                                    {t('Reset appearance')}
                                </Button>
                            </Flex>
                        ),
                    },
                ]}
            />
            {lowContrast && (
                <Alert
                    type="warning"
                    showIcon
                    message={t('Increase the contrast between text and backgrounds.')}
                />
            )}
        </Flex>
    )
}
